import React, { useState } from 'react';
import { ExternalLink, Download, RefreshCw, Globe, Users, FileText, Music, Video, Image, Calendar, User, Copy, Check, Disc3 } from 'lucide-react';
import useSitemap from '../hooks/useSitemap';
import PageHeader from '../components/PageHeader';
import LinkPreview from '../components/LinkPreview';

const Sitemap = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [copied, setCopied] = useState(false);
    
    const { 
        sitemap, 
        loading, 
        error, 
        lastUpdated, 
        refreshSitemap, 
        downloadXMLSitemap, 
        copyXMLSitemap 
    } = useSitemap();

    // Update page meta for SEO
    React.useEffect(() => {
        document.title = 'Sitemap | The Deep Wave';
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.content = 'Complete sitemap of The Deep Wave website including all pages, user profiles, music, videos, images, and events.';
        }
    }, []);

    const handleCopyXML = async () => {
        try {
            await copyXMLSitemap();
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Error copying XML sitemap:', err);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getSectionIcon = (section) => {
        const icons = {
            static: Globe,
            profiles: Users,
            posts: FileText,
            audio: Music,
            video: Video,
            images: Image,
            events: Calendar,
            artists: User,
            albums: Disc3
        };
        return icons[section] || Globe;
    };

    const getSectionTitle = (section) => {
        const titles = {
            static: 'Static Pages',
            profiles: 'User Profiles',
            posts: 'Posts',
            audio: 'Audio Content',
            video: 'Video Content',
            images: 'Image Gallery',
            events: 'Events',
            artists: 'Artist Pages',
            albums: 'Albums'
        };
        return titles[section] || section;
    };

    const getSectionDescription = (section) => {
        const descriptions = {
            static: 'Main website pages and navigation',
            profiles: 'Public user profile pages',
            posts: 'Community posts and discussions',
            audio: 'Music tracks and audio content',
            video: 'Video content and media',
            images: 'Artwork and image gallery',
            events: 'Calendar events and happenings',
            artists: 'Artist-specific content feeds and galleries',
            albums: 'Curated album collections'
        };
        return descriptions[section] || '';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900">
                <PageHeader 
                    title="Loading Sitemap"
                    subtitle="Generating site structure..."
                    icon={<RefreshCw className="w-8 h-8 animate-spin" />}
                />
                <div className="max-w-6xl mx-auto px-6 py-8">
                    <div className="glass-card p-8 text-center">
                        <RefreshCw className="w-12 h-12 text-blue-400 mx-auto mb-4 animate-spin" />
                        <p className="text-gray-400">Generating comprehensive sitemap...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900">
                <PageHeader 
                    title="Sitemap Error"
                    subtitle="Unable to generate sitemap"
                    icon={<Globe className="w-8 h-8" />}
                />
                <div className="max-w-6xl mx-auto px-6 py-8">
                    <div className="glass-card p-8 text-center">
                        <div className="text-red-400 text-4xl mb-4">⚠️</div>
                        <h3 className="text-xl font-semibold text-white mb-2">Error Loading Sitemap</h3>
                        <p className="text-gray-400 mb-6">{error}</p>
                        <button 
                            onClick={refreshSitemap}
                            className="btn-primary"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            <PageHeader 
                title="Website Sitemap"
                subtitle={`Complete site structure • ${sitemap?.metadata?.total_urls || 0} pages indexed • Updated ${lastUpdated}`}
                icon={<Globe className="w-8 h-8" />}
            />

            <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Overview Stats */}
                <div className="glass-card p-6 mb-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div>
                            <h2 className="text-xl font-bold text-white mb-2">Sitemap Overview</h2>
                            <p className="text-gray-400">
                                Auto-generated sitemap for search engines and web crawlers
                            </p>
                            <p className="text-sm text-blue-400 mt-1">
                                Generated: {sitemap?.metadata?.generated ? formatDate(sitemap.metadata.generated) : 'Unknown'}
                            </p>
                        </div>
                        
                        <div className="flex flex-wrap gap-3">
                            <button 
                                onClick={refreshSitemap}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Refresh
                            </button>
                            <button 
                                onClick={handleCopyXML}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        Copy XML
                                    </>
                                )}
                            </button>
                            <button 
                                onClick={downloadXMLSitemap}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                Download XML
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
                    {Object.entries(sitemap || {}).map(([section, items]) => {
                        if (section === 'metadata' || !Array.isArray(items)) return null;
                        
                        const Icon = getSectionIcon(section);
                        return (
                            <button
                                key={section}
                                onClick={() => setActiveTab(section)}
                                className={`glass-card p-4 text-center transition-all hover:border-blue-500/50 ${
                                    activeTab === section ? 'border-blue-500 bg-blue-500/10' : ''
                                }`}
                            >
                                <Icon className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                                <div className="text-lg font-bold text-white">{items.length}</div>
                                <div className="text-xs text-gray-400">{getSectionTitle(section)}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Content Sections */}
                {activeTab === 'overview' ? (
                    <div className="space-y-6">
                        {Object.entries(sitemap || {}).map(([section, items]) => {
                            if (section === 'metadata' || !Array.isArray(items) || items.length === 0) return null;
                            
                            const Icon = getSectionIcon(section);
                            return (
                                <div key={section} className="glass-card p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Icon className="w-6 h-6 text-blue-400" />
                                        <div>
                                            <h3 className="text-lg font-semibold text-white">{getSectionTitle(section)}</h3>
                                            <p className="text-sm text-gray-400">{getSectionDescription(section)}</p>
                                        </div>
                                        <div className="ml-auto">
                                            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium">
                                                {items.length} pages
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 gap-4">
                                        {items.slice(0, 4).map((item, index) => (
                                            <div key={index} className="bg-gray-800/50 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm text-gray-300 truncate mr-2">
                                                        {item.loc.replace(sitemap.metadata.base_url, '')}
                                                    </span>
                                                    <a
                                                        href={item.loc}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-400 hover:text-blue-300 transition-colors flex-shrink-0"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                    </a>
                                                </div>
                                                <div className="text-xs text-gray-500 mb-3">
                                                    Updated: {formatDate(item.lastmod)} • Priority: {item.priority} • Freq: {item.changefreq}
                                                </div>
                                                <LinkPreview 
                                                    url={item.loc} 
                                                    title={item.loc.replace(sitemap.metadata.base_url, '')}
                                                />
                                            </div>
                                        ))}
                                        {items.length > 4 && (
                                            <button
                                                onClick={() => setActiveTab(section)}
                                                className="p-3 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-medium"
                                            >
                                                View all {items.length} {getSectionTitle(section).toLowerCase()}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="glass-card p-6">
                        <div className="flex items-center gap-3 mb-6">
                            {React.createElement(getSectionIcon(activeTab), {
                                className: "w-6 h-6 text-blue-400"
                            })}
                            <div>
                                <h3 className="text-lg font-semibold text-white">{getSectionTitle(activeTab)}</h3>
                                <p className="text-sm text-gray-400">{getSectionDescription(activeTab)}</p>
                            </div>
                            <button
                                onClick={() => setActiveTab('overview')}
                                className="ml-auto text-blue-400 hover:text-blue-300 transition-colors text-sm"
                            >
                                ← Back to overview
                            </button>
                        </div>
                        
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                            {(sitemap[activeTab] || []).map((item, index) => (
                                <div key={index} className="bg-gray-800/50 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex-1 min-w-0 mr-4">
                                            <div className="text-sm text-gray-300 truncate">
                                                {item.loc.replace(sitemap.metadata.base_url, '')}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Updated: {formatDate(item.lastmod)} • Priority: {item.priority} • Freq: {item.changefreq}
                                            </div>
                                        </div>
                                        <a
                                            href={item.loc}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:text-blue-300 transition-colors flex-shrink-0"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    </div>
                                    <LinkPreview 
                                        url={item.loc} 
                                        title={item.loc.replace(sitemap.metadata.base_url, '')}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* SEO Information */}
                <div className="glass-card p-6 mt-8">
                    <h3 className="text-lg font-semibold text-white mb-4">SEO Information</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-400">XML Sitemap URL:</span>
                            <a
                                href={`${sitemap?.metadata?.base_url}/sitemap.xml`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 bg-gray-800/50 px-2 py-1 rounded transition-colors"
                            >
                                <code>{sitemap?.metadata?.base_url}/sitemap.xml</code>
                            </a>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-400">Last Updated:</span>
                            <span className="text-gray-300">
                                {sitemap?.metadata?.generated ? formatDate(sitemap.metadata.generated) : 'Unknown'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-400">Total URLs:</span>
                            <span className="text-gray-300">{sitemap?.metadata?.total_urls || 0}</span>
                        </div>
                    </div>
                    
                    <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <h4 className="text-blue-400 font-medium mb-2">📄 Submit to Search Engines</h4>
                        <p className="text-sm text-gray-400 mb-3">
                            Submit this sitemap to Google Search Console and Bing Webmaster Tools for better indexing:
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="https://search.google.com/search-console"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 text-sm underline"
                            >
                                Google Search Console
                            </a>
                            <a
                                href="https://www.bing.com/webmasters"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 text-sm underline"
                            >
                                Bing Webmaster Tools
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sitemap;